import { firebase } from "../firebase/initFirebase";

export const isShortProductBarcode = (value) => /^[1-9][0-9]{9}$/.test(value);
const resolvedCodes = new Map();

// Allocation and lookup live in one registry; codes are never reused, even after deletion.
export async function ensureProductBarcode(productId) {
  if (!productId || String(productId).includes("/")) throw new Error("Invalid product ID");
  const db = firebase.firestore();
  const registry = db.collection("product_barcodes");
  const productRef = db.collection("products").doc(productId);
  const counterRef = registry.doc("_counter");
  return db.runTransaction(async (transaction) => {
    const product = await transaction.get(productRef);
    if (!product.exists) throw new Error("Product no longer exists");
    const existing = product.data().barcode_number;
    if (existing) {
      if (!isShortProductBarcode(existing)) throw new Error("Invalid stored barcode number");
      const mapping = await transaction.get(registry.doc(existing));
      if (!mapping.exists || mapping.data().product_id !== productId) {
        throw new Error("Product barcode mapping is inconsistent");
      }
      return existing;
    }
    const counter = await transaction.get(counterRef);
    let next = counter.exists ? counter.data().next_number : 1000000000;
    if (!Number.isSafeInteger(next) || next < 1000000000 || next > 9999999999) {
      throw new Error("Product barcode number range exhausted");
    }
    let code = String(next);
    let mappingRef = registry.doc(code);
    let mapping = await transaction.get(mappingRef);
    // Admin-assigned numbers are reserved too; allocation must skip them forever.
    while (mapping.exists) {
      next += 1;
      if (next > 9999999999) throw new Error("Product barcode number range exhausted");
      code = String(next);
      mappingRef = registry.doc(code);
      mapping = await transaction.get(mappingRef);
    }
    transaction.set(mappingRef, { product_id: productId });
    transaction.set(counterRef, { next_number: next + 1 });
    transaction.update(productRef, { barcode_number: code });
    return code;
  });
}

export async function resolveProductScan(value) {
  const text = String(value || "").trim();
  // Legacy product QR/barcodes and package payloads retain their original routing.
  if (!isShortProductBarcode(text)) return text;
  if (resolvedCodes.has(text)) return `prod_${resolvedCodes.get(text)}`;
  const mapping = await firebase.firestore().collection("product_barcodes").doc(text).get();
  if (!mapping.exists || !mapping.data().product_id) {
    throw new Error("Product barcode not found");
  }
  const productId = mapping.data().product_id;
  resolvedCodes.set(text, productId);
  return `prod_${productId}`;
}

export async function checkProductBarcode(number, productId) {
  const code = String(number || "").trim();
  if (!isShortProductBarcode(code)) throw new Error("Enter 10 digits; the first digit must be 1-9.");
  const mapping = await firebase.firestore().collection("product_barcodes").doc(code).get();
  return !mapping.exists || mapping.data().product_id === productId;
}

export async function updateProductBarcode(productId, number) {
  const code = String(number || "").trim();
  if (!isShortProductBarcode(code)) throw new Error("Enter 10 digits; the first digit must be 1-9.");
  if (!productId || String(productId).includes("/")) throw new Error("Invalid product ID");
  const uid = firebase.auth().currentUser?.uid;
  if (!uid) throw new Error("Only admins can edit product barcodes.");
  const db = firebase.firestore();
  return db.runTransaction(async (transaction) => {
    // Read the authenticated user's stored role rather than trusting a UI prop.
    const user = await transaction.get(db.collection("users").doc(uid));
    if (!user.exists || user.data().role !== "admin") {
      throw new Error("Only admins can edit product barcodes.");
    }
    const productRef = db.collection("products").doc(productId);
    const product = await transaction.get(productRef);
    if (!product.exists) throw new Error("Product no longer exists");
    const mappingRef = db.collection("product_barcodes").doc(code);
    const mapping = await transaction.get(mappingRef);
    if (mapping.exists && mapping.data().product_id !== productId) {
      throw new Error("This barcode already exists for another product.");
    }
    // Retain the old mapping as an alias so previously printed labels still scan.
    transaction.set(mappingRef, { product_id: productId });
    transaction.update(productRef, { barcode_number: code });
    return code;
  });
}
